/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Meta Conversions API", keywords = "Facebook Conversions API, Facebook Ads", lastUpdatedDate = new Date(1764806400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Meta Conversions API",
        description: null,
        lastUpdatedDate: "Thu Dec 04 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "facebook-conversions",
        type: "destination",
        icon: null,
        toc: [{
          "value": "When to use Meta Conversions API",
          "depth": 0,
          "id": "when-to-use-meta-conversions-api"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Meta Conversions API",
          "depth": 0,
          "id": "connect-to-meta-conversions-api"
        }, {
          "value": "Configure the sync",
          "depth": 0,
          "id": "configure-the-sync"
        }, {
          "value": "Required event fields",
          "depth": 1,
          "id": "required-event-fields"
        }, {
          "value": "Event Data Quality",
          "depth": 0,
          "id": "event-data-quality"
        }, {
          "value": "Event Match Quality (EMQ)",
          "depth": 1,
          "id": "event-match-quality-emq"
        }, {
          "value": "Additional Conversions Reported",
          "depth": 1,
          "id": "additional-conversions-reported"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      strong: "strong",
      ul: "ul",
      li: "li",
      hr: "hr",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      ol: "ol",
      img: "img",
      h3: "h3",
      code: "code",
      h4: "h4"
    }, _provideComponents(), props.components), {EventSyncWarning, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "when-to-use-meta-conversions-api",
        children: "When to use Meta Conversions API"
      }), "\n", _jsx(_components.p, {
        children: "Use this destination to improve Meta campaign performance and measurement by sending offline or server-side conversion events (for example, purchases, form fills, or calls) to Meta."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Use Meta Conversions API when you want to:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Attribute conversions that happen outside the browser (for example, in your CRM or POS system)"
        }), "\n", _jsx(_components.li, {
          children: "Improve ad delivery and targeting with more complete conversion data"
        }), "\n", _jsx(_components.li, {
          children: "Measure performance when cookies or browser signals are limited"
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Events"
              })
            }), _jsx(_components.td, {
              children: "Sync conversion events to Meta's Conversions API"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.facebook.com/docs/marketing-api/conversions-api/",
                children: "Conversions API reference"
              })
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "Sync modes"
        }), " for more information."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-meta-conversions-api",
        children: "Connect to Meta Conversions API"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Meta Conversions"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to the Meta Conversions API by entering the following required fields into Hightouch:"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In Hightouch, go to ", _jsx(_components.a, {
            href: "https://app.hightouch.com/destinations?from=docs",
            children: _jsx(_components.strong, {
              children: "Destinations"
            })
          }), " and click ", _jsx(_components.strong, {
            children: "Add destination"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Meta Conversions API"
          }), " and click ", _jsx(_components.strong, {
            children: "Continue"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Enter the following credentials:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Pixel ID"
              }), " or ", _jsx(_components.strong, {
                children: "Database ID"
              })]
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.strong, {
                children: "Access Token"
              })
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-meta-conversion-pixel.png",
          alt: "Pixel ID in Events Manager"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Find these in ", _jsx(_components.a, {
          href: "https://business.facebook.com/events_manager2/",
          children: "Meta Events Manager"
        }), ":"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Open the ", _jsx(_components.strong, {
            children: "Data Sources"
          }), " tab and select your Pixel."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Settings"
          }), ", find your ", _jsx(_components.strong, {
            children: "Pixel ID"
          }), " in the ", _jsx(_components.strong, {
            children: "Details"
          }), " section."]
        }), "\n", _jsxs(_components.li, {
          children: ["To create an Access Token, scroll to ", _jsx(_components.strong, {
            children: "Conversions API"
          }), " and click ", _jsx(_components.strong, {
            children: "Generate access token"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-meta-conversion-token.png",
          alt: "Access token in Meta Events Manager"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "configure-the-sync",
        children: "Configure the sync"
      }), "\n", _jsxs(_components.p, {
        children: ["After connecting Meta Conversions API and selecting a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), ", create a sync to start sending data."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.a, {
            href: "https://app.hightouch.com/syncs?from=docs",
            children: _jsx(_components.strong, {
              children: "Syncs"
            })
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Add sync"
          }), ", choose your model, and select ", _jsx(_components.strong, {
            children: "Meta Conversions API"
          }), " as the destination."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "required-event-fields",
        children: "Required event fields"
      }), "\n", _jsx(_components.p, {
        children: "The Meta Conversions API requires the following event parameters:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "event_name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "event_time"
          })
        }), "\n", _jsxs(_components.li, {
          children: ["At least one ", _jsxs(_components.a, {
            href: "https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/customer-information-parameters",
            children: ["customer information (", _jsx(_components.code, {
              children: "user_data"
            }), ")"]
          }), " field"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The sync configuration form ensures all these are set and provides some additional options."
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h4, {
        id: "event-name",
        children: "Event name"
      }), "\n", _jsxs(_components.p, {
        children: ["Providing an event name is required to send an event to the Meta Conversions API. You can either provide a static value or select to ", _jsx(_components.strong, {
          children: "use a column"
        }), " from your model. Hightouch syncs the static or column value as the ", _jsx(_components.code, {
          children: "event_name"
        }), " parameter that the Conversions API requires."]
      }), "\n", _jsxs(_components.p, {
        children: ["Use Meta's ", _jsx(_components.a, {
          href: "https://www.facebook.com/business/help/402791146561655?id=1205376682832142",
          children: "Standard Events"
        }), " when possible (for example, ", _jsx(_components.code, {
          children: "Purchase"
        }), ", ", _jsx(_components.code, {
          children: "Lead"
        }), ", or ", _jsx(_components.code, {
          children: "AddToCart"
        }), ") to improve reporting accuracy."]
      }), "\n", _jsx(_components.h4, {
        id: "event-time",
        children: "Event time"
      }), "\n", _jsxs(_components.p, {
        children: ["You can optionally select a column that contains timestamps of when events occurred. If this field is empty, Hightouch uses the time the event arrives at the server. Hightouch syncs the timestamp as the ", _jsx(_components.code, {
          children: "event_time"
        }), " parameter the Conversions API requires."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you select a column, it should be in ", _jsx(_components.a, {
          href: "https://www.iso.org/iso-8601-date-and-time-format.html",
          children: "ISO 8601"
        }), " format. Hightouch will then automatically convert the data into the format that Meta expects."]
      }), "\n", _jsx(_components.h4, {
        id: "action-source",
        children: "Action source"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/server-event#action-source",
          children: _jsx(_components.code, {
            children: "action_source"
          })
        }), " parameter specifies where your conversions occurred (for example, web, app, email)."]
      }), "\n", _jsx(_components.p, {
        children: "Knowing where your events took place helps Meta optimize delivery and attribution."
      }), "\n", _jsx(_components.p, {
        children: "Certain source types require specific fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Web"
          }), " events must include ", _jsx(_components.code, {
            children: "event_source_url"
          }), " and ", _jsx(_components.code, {
            children: "client_user_agent"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "App"
          }), " events must include ", _jsx(_components.code, {
            children: "advertiser_tracking_enabled"
          }), ", ", _jsx(_components.code, {
            children: "application_tracking_enabled"
          }), ", and ", _jsx(_components.code, {
            children: "extinfo"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If not all rows contain this field, you can set a ", _jsx(_components.strong, {
          children: "fallback action source"
        }), " in your sync configuration."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mappings",
        children: "Field mappings"
      }), "\n", _jsx(_components.p, {
        children: "Map your customer information and event parameters here."
      }), "\n", _jsxs(_components.p, {
        children: ["The Meta Conversions API requires at least one ", _jsx(_components.a, {
          href: "https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/customer-information-parameters",
          children: _jsx(_components.code, {
            children: "user_data"
          })
        }), " parameter for attribution."]
      }), "\n", _jsxs(_components.p, {
        children: ["Include as many ", _jsx(_components.code, {
          children: "user_data"
        }), " fields as possible to improve ", _jsx(_components.a, {
          href: "https://www.facebook.com/business/help/765081237991954?id=818859032317965",
          children: "Event Match Quality"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["If using a ", _jsx(_components.strong, {
          children: "Standard Event"
        }), " (like ", _jsx(_components.code, {
          children: "Purchase"
        }), "), ensure you include all required fields (for example, ", _jsx(_components.code, {
          children: "value"
        }), ", ", _jsx(_components.code, {
          children: "currency"
        }), ") and any relevant optional ones."]
      }), "\n", _jsxs(_components.p, {
        children: ["Custom fields are sent in the ", _jsx(_components.code, {
          children: "custom_data"
        }), " parameter."]
      }), "\n", _jsx(_components.h4, {
        id: "advanced-analytics",
        children: "Advanced analytics"
      }), "\n", _jsx(_components.p, {
        children: "To send data through the Conversions API to the Advanced Measurement API (AMAPI), map the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "data_processing_options"
          }), ": an array of strings like ", _jsx(_components.code, {
            children: "[\"AMO\"]"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "advanced_measurement_table"
          }), ": a string"]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "test-event-codes",
        children: "Test event codes"
      }), "\n", _jsx(_components.p, {
        children: "Test event codes are special codes you can include in your event payload to simulate conversion events. This allows you to test your API implementation without actually recording real conversion data."
      }), "\n", _jsxs(_components.p, {
        children: ["You can optionally enter a test event code as part of your sync configuration. You can find your test event code in your Meta ", _jsx(_components.a, {
          href: "https://business.facebook.com/events_manager2/",
          children: "Events Manager"
        }), " in the ", _jsx(_components.strong, {
          children: "Test events"
        }), " tab."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-meta-conversion-test-event-code.png",
          alt: "Test event code in Meta Events Manager"
        })
      }), "\n", _jsx(_components.p, {
        children: "Make sure to remove the test event code if you're running a production sync."
      }), "\n", _jsx(_components.h4, {
        id: "hashing",
        children: "Hashing"
      }), "\n", _jsx(_components.p, {
        children: "By default, Hightouch automatically hashes the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "email"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "phone"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "gender"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "dob"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "last name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "first name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "city"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "state"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "zip"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "country"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If you want to turn hashing off, select ", _jsx(_components.strong, {
          children: "No"
        }), " under ", _jsx(_components.strong, {
          children: "Would you like Hightouch to automatically detect if your PII data requires hashing?"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "event-data-quality",
        children: "Event Data Quality"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch provides insight into the effectiveness of your conversion API implementation by reporting on ", _jsx(_components.strong, {
          children: "Event Match Quality (EMQ)"
        }), " and ", _jsx(_components.strong, {
          children: "Additional Conversions Reported"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Data quality metrics appear in the ", _jsx(_components.strong, {
          children: "Overview"
        }), " tab of your Meta Conversions API sync."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Your access token must include Data Quality API permissions. If metrics don't appear, generate a new token in Meta Events Manager."
        })
      }), "\n", _jsx(_components.h3, {
        id: "event-match-quality-emq",
        children: "Event Match Quality (EMQ)"
      }), "\n", _jsxs(_components.p, {
        children: ["The EMQ score estimates how well your conversion events match to Meta users, ad clicks, and impressions. It's based on the \"fill rate\" of key ", _jsx(_components.code, {
          children: "user_data"
        }), " values you send with each conversion event."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination--conversion-emq.png",
          alt: "Meta Event Match Quality example"
        })
      }), "\n", _jsx(_components.p, {
        children: "A higher EMQ score generally means better attribution accuracy and ad performance, as Meta's algorithms can use more complete data to optimize bids, budgets, and targeting."
      }), "\n", _jsx(_components.p, {
        children: "To improve your EMQ score:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Collect and sync more ", _jsx(_components.code, {
            children: "user_data"
          }), " fields with each event."]
        }), "\n", _jsxs(_components.li, {
          children: ["Use ", _jsx(_components.a, {
            href: "/match-booster/overview",
            children: _jsx(_components.strong, {
              children: "Match Booster"
            })
          }), " to supplement user data with Hightouch's third-party identity graph."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For guidance on which user attributes to prioritize, see the ", _jsx(_components.a, {
          href: "https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/customer-information-parameters/",
          children: "Conversion API schema guide"
        }), ". For more about EMQ, see ", _jsx(_components.a, {
          href: "https://www.facebook.com/business/help/765081237991954?id=818859032317965",
          children: "Meta's documentation"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "additional-conversions-reported",
        children: "Additional Conversions Reported"
      }), "\n", _jsx(_components.p, {
        children: "The Additional Conversions Reported metric helps you understand how much your business benefits from using the Conversions API alongside the Meta Pixel. More reported conversions can help you decrease your cost per result and show your ads to people who find them relevant."
      }), "\n", _jsxs(_components.p, {
        children: ["Learn more in ", _jsx(_components.a, {
          href: "https://www.facebook.com/business/help/409195343791109",
          children: "Meta's Additional Conversions Reported guide"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "meta-sdk",
        children: "Meta SDK"
      }), "\n", _jsx(_components.p, {
        children: "When setting up a new datasource for CAPI, if you don't want to use the Meta SDK in your app, choose “Web” (even if the data would include app events as well). This eliminates the need to install the SDK."
      }), "\n", _jsxs(_components.p, {
        children: ["In this case, use ", _jsx(_components.strong, {
          children: "Dataset ID"
        }), " instead of ", _jsx(_components.strong, {
          children: "Pixel ID"
        }), " when configuring the destination."]
      }), "\n", _jsx(_components.h4, {
        id: "app-data",
        children: "App data"
      }), "\n", _jsxs(_components.p, {
        children: ["To send app data in your CAPI events, include ", _jsx(_components.a, {
          href: "https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/app-data#",
          children: "app data parameters"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
